import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { BiArrowBack } from 'react-icons/bi';
import { platformAPI } from '../../services/platformAPI';
import WorkspaceDataTable from '../../components/workspace/WorkspaceDataTable';
import WorkspacePageHeader from '../../components/workspace/WorkspacePageHeader';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { usersListPath } from './settings/settingsPaths';
import './LoginHistoryPage.scss';

const FILTERS = [
  { id: 'all', label: 'Everyone' },
  { id: 'in', label: 'Signed in' },
  { id: 'out', label: 'Signed out' }
];

function formatWhen(value) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const LoginHistoryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const listPath = usersListPath(location.pathname);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [people, setPeople] = useState([]);
  const [filter, setFilter] = useState('all');

  useDocumentTitle('Login history');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await platformAPI.loginHistory();
        if (cancelled) return;
        setSummary(response.data?.data?.summary || null);
        setPeople(response.data?.data?.people || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Unable to load login history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const rows = useMemo(() => people.filter((person) => {
    if (filter === 'in') return person.signedIn;
    if (filter === 'out') return !person.signedIn;
    return true;
  }).map((person) => ({
    ...person,
    rolesLabel: (person.roles || []).join(', ') || '—',
    sessionLabel: person.signedIn ? 'Signed in' : 'Signed out',
    lastLabel: formatWhen(person.lastLoginAt),
    ipLabel: person.ipAddress || '—'
  })), [people, filter]);

  const facts = [
    { label: 'Active users', value: summary?.activeUsers ?? 0 },
    { label: 'Active roles', value: summary?.activeRoles ?? 0 },
    { label: 'Administrators', value: summary?.administrators ?? 0 },
    { label: 'Signed in', value: summary?.signedIn ?? 0, tone: 'live' },
    { label: 'Offices', value: summary?.locations ?? 0 }
  ];

  const columns = useMemo(() => [
    { key: 'name', label: 'Name', mobileTitle: true },
    { key: 'type', label: 'Type' },
    { key: 'rolesLabel', label: 'Role' },
    { key: 'email', label: 'Email' },
    {
      key: 'accountStatus',
      label: 'Account',
      render: (value) => (
        <span className={`login-report__account${value === 'Active' ? ' is-active' : ''}`}>{value}</span>
      )
    },
    {
      key: 'sessionLabel',
      label: 'Session',
      render: (_, row) => (
        <span className={`login-report__session${row.signedIn ? ' is-in' : ''}`}>
          <i aria-hidden />
          {row.sessionLabel}
        </span>
      )
    },
    { key: 'lastLabel', label: 'Last sign-in' },
    {
      key: 'ipLabel',
      label: 'Address',
      render: (value) => <span className="login-report__ip">{value}</span>
    }
  ], []);

  return (
    <div className="login-report">
      <WorkspacePageHeader
        title="Login history"
        subtitle="Who can sign in, who is signed in now, and the address of their latest sign-in."
        actions={(
          <Button size="sm" variant="outline-primary" onClick={() => navigate(listPath)}>
            <BiArrowBack className="me-1" />
            Users
          </Button>
        )}
      />

      {error ? <Alert variant="danger">{error}</Alert> : null}

      {loading ? (
        <div className="py-4 text-center"><Spinner animation="border" size="sm" /></div>
      ) : (
        <>
          <div className="login-report__facts">
            {facts.map((fact) => (
              <article key={fact.label} className={`login-report__fact${fact.tone ? ` login-report__fact--${fact.tone}` : ''}`}>
                <strong>{fact.value}</strong>
                <span>{fact.label}</span>
              </article>
            ))}
          </div>

          <div className="login-report__filters" role="tablist" aria-label="Filter people">
            {FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={filter === item.id ? 'is-on' : ''}
                onClick={() => setFilter(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <WorkspaceDataTable
            title="People"
            downloadPermission="users.download"
            data={rows}
            columns={columns}
            searchPlaceholder="Search name, role, or email…"
            emptyMessage="No people match this view"
            pageSize={10}
            showActions={false}
            showCheckboxes={false}
          />
        </>
      )}
    </div>
  );
};

export default LoginHistoryPage;
